export interface SettingsNavGate {
  /** usePrivilegeCheck resource, e.g. "settings.webhooks" */
  privilege?: string;
  /** access levels satisfying the privilege; defaults to ["view","create","manage"] */
  accessLevels?: string[];
  /** useAccountHasEntitlement string, e.g. "workflow-approval" */
  entitlement?: string;
  /** useFlags() camelCase key, e.g. "releaseByok" */
  flag?: string;
}

export interface SettingsNavChild {
  label: string;
  /** Absolute admin/site URL. `react` navigates client-side inside
   *  SettingsAppLayout; `rails` does a full-page GET. */
  path: string;
  id: string;
  routing: "react" | "rails";
  gate?: SettingsNavGate;
}

export interface SettingsNavSection {
  label: string;
  id: string;
  children: SettingsNavChild[];
  /** Only the Site Admin section; hidden for non-site-admins */
  siteAdminOnly?: boolean;
}

export interface ActiveNav {
  sectionId: string;
  childId: string;
}

const settingsNavItems: SettingsNavSection[] = [
  {
    label: "Account",
    id: "settings-account",
    children: [
      {
        label: "Account Info",
        path: "/admin/account_info",
        id: "settings-account-info",
        routing: "react",
        gate: { privilege: "settings.account", accessLevels: ["manage"] },
      },
      {
        label: "Account Preferences",
        path: "/admin/account_preferences",
        id: "settings-account-preferences",
        routing: "react",
        gate: { privilege: "settings.account", accessLevels: ["manage"] },
      },
      {
        label: "Notification Policies",
        path: "/admin/account_notifications",
        id: "settings-account-notifications",
        routing: "react",
        gate: {
          privilege: "settings.account",
          accessLevels: ["manage"],
          flag: "releaseNotificationManagement",
        },
      },
      {
        label: "Account Credits",
        path: "/admin/account_credits",
        id: "settings-account-credits",
        routing: "react",
        gate: {
          privilege: "settings.account",
          accessLevels: ["manage"],
          flag: "configAccountCredits",
        },
      },
      {
        label: "Connected Apps",
        path: "/admin/account_connections",
        id: "settings-connections",
        routing: "react",
        gate: { privilege: "settings.connected_apps" },
      },
      {
        label: "Partnerships",
        path: "/admin/partnerships",
        id: "settings-partnerships",
        routing: "rails",
        gate: {
          privilege: "settings.partnerships",
          flag: "configShowPartnerships",
        },
      },
      {
        label: "Webhooks",
        path: "/admin/webhook_subscriptions",
        id: "settings-webhooks",
        routing: "react",
        gate: { privilege: "settings.webhooks" },
      },
      {
        label: "AI Prompts",
        path: "/admin/prompt_overrides",
        id: "settings-prompt-overrides",
        routing: "react",
        gate: {
          privilege: "settings.prompt_overrides",
          accessLevels: ["manage"],
        },
      },
      {
        label: "Private LLM Keys",
        path: "/admin/open_router_keys",
        id: "settings-open-router-keys",
        routing: "react",
        gate: { flag: "releaseByok" },
      },
      {
        label: "OAuth Callback URL",
        // `:slug` is substituted with the current account slug at render time
        path: "/site/accounts/:slug/account_callback/edit",
        id: "settings-account-callback",
        routing: "react",
        gate: {
          privilege: "settings.account",
          accessLevels: ["manage"],
          flag: "configCustomOauth",
        },
      },
    ],
  },
  {
    label: "Content",
    id: "settings-content",
    children: [
      {
        label: "Lines of Business",
        path: "/admin/sections",
        id: "settings-lob",
        routing: "react",
        gate: { privilege: "settings.lines_of_business" },
      },
      {
        label: "Language Fields",
        path: "/admin/language_fields",
        id: "settings-language-fields",
        routing: "react",
        gate: { privilege: "settings.language_fields" },
      },
      {
        label: "Terms and Conditions",
        path: "/admin/service_conditions",
        id: "settings-service-conditions",
        routing: "react",
        gate: { privilege: "settings.terms_and_conditions" },
      },
      {
        label: "Professional Services",
        path: "/admin/tasks",
        id: "settings-tasks",
        routing: "react",
        gate: { privilege: "settings.tasks" },
      },
      {
        label: "Managed Services",
        path: "/admin/managed_services",
        id: "settings-managed-services",
        routing: "react",
        gate: { privilege: "settings.managed_services" },
      },
      {
        label: "Blueprints",
        path: "/admin/blueprints",
        id: "settings-blueprints",
        routing: "react",
        gate: { privilege: "settings.blueprints" },
      },
      {
        label: "Product",
        path: "/admin/materials",
        id: "settings-materials",
        routing: "react",
        gate: { privilege: "settings.materials" },
      },
    ],
  },
  {
    label: "Documents",
    id: "settings-documents",
    children: [
      {
        label: "Document Templates",
        path: "/admin/document_templates",
        id: "settings-sow-templates",
        routing: "react",
      },
      {
        label: "Text Blocks",
        path: "/admin/text_blocks",
        id: "settings-text-blocks",
        routing: "react",
        gate: { privilege: "settings.text_blocks" },
      },
      {
        label: "Attachment Types",
        path: "/admin/attachment_types",
        id: "settings-attachment-types",
        routing: "react",
        gate: { privilege: "settings.attachment_types" },
      },
      {
        label: "Surveys",
        path: "/admin/questionnaires",
        id: "settings-questionnaires",
        routing: "react",
        gate: { privilege: "settings.questionnaires" },
      },
    ],
  },
  {
    label: "Financial",
    id: "settings-financial",
    children: [
      {
        label: "Currencies",
        path: "/admin/currencies",
        id: "settings-currencies",
        routing: "react",
        gate: { privilege: "settings.currencies" },
      },
      {
        label: "Resources",
        path: "/admin/resources",
        id: "settings-resources",
        routing: "react",
        gate: { privilege: "settings.resources" },
      },
      {
        label: "Rate Tables",
        path: "/admin/rate_tables",
        id: "settings-rate-tables",
        routing: "react",
        gate: { privilege: "settings.resources" },
      },
      {
        label: "Travel/Expense",
        path: "/admin/expense_categories",
        id: "settings-expenses",
        routing: "react",
        gate: { privilege: "settings.travel_and_expense" },
      },
      {
        label: "Payment Terms",
        path: "/admin/payment_terms",
        id: "settings-payment-terms",
        routing: "react",
        gate: { privilege: "settings.payment_terms" },
      },
      {
        label: "Payment Credits",
        path: "/admin/payment_credits",
        id: "settings-payment-credits",
        routing: "react",
        gate: { privilege: "settings.payment_credits" },
      },
    ],
  },
  {
    label: "Governance",
    id: "settings-governance-section",
    children: [
      {
        label: "Project Governance",
        path: "/admin/governances",
        id: "settings-governance",
        routing: "react",
        gate: { privilege: "settings.project_governance" },
      },
      {
        label: "Project Phases",
        path: "/admin/phases",
        id: "settings-phases",
        routing: "react",
        gate: { privilege: "settings.project_phases" },
      },
      {
        label: "Project Variables",
        path: "/admin/project_variables",
        id: "settings-variables",
        routing: "react",
        gate: { privilege: "settings.project_variables" },
      },
      {
        label: "Project Approval",
        path: "/admin/approval_levels",
        id: "settings-approval",
        routing: "react",
        gate: {
          privilege: "settings.project_approval",
          entitlement: "workflow-approval",
        },
      },
    ],
  },
  {
    label: "Users & Groups",
    id: "settings-users-groups",
    children: [
      {
        label: "Users",
        path: "/admin/users",
        id: "settings-users-list",
        routing: "react",
        gate: { privilege: "settings.users" },
      },
      {
        label: "Sales Executives",
        path: "/admin/sales_executives",
        id: "settings-users",
        routing: "react",
        gate: { privilege: "settings.sales_executives" },
      },
      {
        label: "Roles",
        path: "/admin/roles",
        id: "settings-roles",
        routing: "react",
        gate: { privilege: "settings.roles" },
      },
      {
        label: "Teams",
        path: "/admin/teams",
        id: "settings-teams",
        routing: "react",
        gate: { privilege: "settings.teams" },
      },
      {
        label: "Business Units",
        path: "/admin/business_units",
        id: "settings-business-units",
        routing: "react",
        gate: { privilege: "settings.business_units" },
      },
      {
        label: "Clients",
        path: "/admin/clients",
        id: "settings-clients",
        routing: "react",
        gate: { privilege: "settings.clients" },
      },
      {
        label: "Vendors",
        path: "/admin/vendors",
        id: "settings-vendors",
        routing: "react",
        gate: {
          privilege: "settings.vendors",
          entitlement: "3rd-party-scoping",
        },
      },
    ],
  },
  {
    label: "Site Admin",
    id: "settings-site-admin",
    siteAdminOnly: true,
    children: [
      {
        label: "Accounts",
        path: "/site/accounts",
        id: "settings-all-accounts",
        routing: "rails",
      },
      {
        label: "Free Trials",
        path: "/admin/free_trials",
        id: "settings-free-accounts",
        routing: "rails",
      },
      {
        label: "SAML Configuration",
        path: "/site/saml_configurations",
        id: "settings-saml-config",
        routing: "rails",
      },
      {
        label: "OAuth Callbacks",
        path: "/site/oauth_callbacks",
        id: "settings-oauth-callbacks",
        routing: "rails",
      },
      {
        label: "AI Prompts",
        path: "/site/prompt_contents",
        id: "settings-prompt-contents",
        routing: "rails",
      },
      {
        label: "Release Flags",
        path: "/admin/release_flags",
        id: "settings-release-flags",
        routing: "react",
      },
      {
        label: "All Users",
        path: "/site/users",
        id: "settings-all-users",
        routing: "rails",
      },
    ],
  },
];

/**
 * Returns the active section/child for a pathname by longest-prefix match:
 * a child matches when the pathname equals its path or begins with
 * `<path>/`. The longest matching path wins.
 */
export const resolveActiveNav = (pathname: string): ActiveNav | null => {
  let best: ActiveNav | null = null;
  let bestLength = 0;

  settingsNavItems.forEach((section) => {
    section.children.forEach((child) => {
      // Paths with a `:param` segment (e.g. the account slug) match any value
      // in that segment; static paths match exactly or as a prefix.
      const matches = child.path.includes(":")
        ? new RegExp(`^${child.path.replace(/:[^/]+/g, "[^/]+")}(?:/|$)`).test(
            pathname
          )
        : pathname === child.path || pathname.startsWith(`${child.path}/`);
      if (matches && child.path.length > bestLength) {
        best = { sectionId: section.id, childId: child.id };
        bestLength = child.path.length;
      }
    });
  });

  return best;
};

export default settingsNavItems;
